import { useCallback, useEffect, useRef, useState } from "react";
import { shallowEqual } from "react-redux";
import type { CarConfigDispatchType } from "../redux/store";
import { useCarConfigSelector } from "../redux/store";
import type { T1InjectionType } from "../../../../common-deprecated/hooks/useT1ModalInjection";
import { getExternalConfigurationObject } from "../utils/typedUtils";
import { updateInsuranceQuote } from "../redux/slices/CarConfigFinanceSlice";
import { clearModal, updateModalSettings } from "../../../../common-deprecated/redux/actions/ModalActions";
import { trackOpenInsuranceCalculator } from "../utils/tracking";
import { useAphStepTracking } from "./useAphStepTracking";
import { MODAL_INSURANCE_CALCULATOR } from "../utils/modalConstants";
import { CarConfigConfigureSection } from "../utils/types/SectionTypes";
import { useDispatch } from "../../../../shared-logic/redux/store";

const useAphInsuranceCalculatorButton = (): {
    showCalculator: () => void;
    isT1InjectionReady: boolean;
    onClose: () => void;
    containerId: string;
} => {
    // ----------------------------------------------------------------------
    // Redux data
    // ----------------------------------------------------------------------
    const dispatch = useDispatch<CarConfigDispatchType>();
    const t1InjectionComponents = useRef<T1InjectionType>();
    const [isT1InjectionReady, setIsT1InjectionReady] = useState<boolean>(false);

    const insuranceCalculatorUrl = useCarConfigSelector((state) => state.carConfigSettings.insuranceCalculatorUrl);
    const carConfigSettings = useCarConfigSelector((state) => state.carConfigSettings);
    const ccSaveConfig = useCarConfigSelector(getExternalConfigurationObject, shallowEqual);

    const containerId = "mlb-tim-container";

    // We also listen to the TIM_WIDGET_CLOSE event as the Makolab calculator can dispatch it to close the modal.
    // (yes these should probably be separate events but that ship has sailed at this point)
    // Attach a 'closed' property to avoid double events (see TIM_WIDGET_CLOSE listener & onClose below)
    const event = new CustomEvent("TIM_WIDGET_CLOSED", { detail: { closed: false } });

    const trackStep = useAphStepTracking();

    const fetchFinanceConfig = useCallback(async () => {
        const fallbackGateway = `${carConfigSettings.loadSaveServer}/config/find/json/finance`;
        const ccSaveResponse = await fetch(fallbackGateway, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(ccSaveConfig),
            mode: "cors",
        });

        const response = await ccSaveResponse.json();
        return response;
    }, [ccSaveConfig]);

    // ----------------------------------------------------------------------
    // Injection logic
    // ----------------------------------------------------------------------
    // Load T1 injection logic, listen to insuranceQuoteId updates.
    useEffect(() => {
        if (insuranceCalculatorUrl) {
            window.require([insuranceCalculatorUrl], (components) => {
                t1InjectionComponents.current = components;
            });
            setIsT1InjectionReady(true);
        }
    }, [insuranceCalculatorUrl]);

    const onCloseWrapper = (): void => onClose();

    const onSaveWrapper = (data: unknown): void => {
        const insuranceQuote = (data as { detail: { quoteId: string } }).detail.quoteId;
        dispatch(updateInsuranceQuote({ insurance: insuranceQuote }));
        onClose();
    };

    const onClose = (): void => {
        const timContainer = document.getElementById(containerId);
        // This event is needed. Cleans up code on makolab side & triggers tracking.
        document.dispatchEvent(new CustomEvent("bss-destroy-connector-event"));
        if (timContainer && event && !event.detail.closed) {
            // Without detail.closed, this would be triggered twice. Once from our onClose trigger, and once from Makolab's TIM_WIDGET_CLOSED trigger.
            // Clear the modal as the injection will cause troubles second time around otherwise
            dispatch(clearModal(MODAL_INSURANCE_CALCULATOR));
            event.detail.closed = true;
            timContainer.dispatchEvent(event);
        }
    };

    const showCalculator = async (): Promise<void> => {
        const financeConfig = await fetchFinanceConfig();

        trackStep(CarConfigConfigureSection.Services);
        trackOpenInsuranceCalculator();

        const timContainer = document.getElementById(containerId);
        // Wait for the injection script to fully initialize
        window.requestAnimationFrame(() => {
            window.require(
                insuranceCalculatorUrl,
                (insuranceCalculatorFns) => {
                    if (timContainer) {
                        insuranceCalculatorFns.inject({
                            target: `#${containerId}`,
                            isAppendedToBody: false,
                            configuration: financeConfig,
                        });

                        timContainer.addEventListener("TIM_WIDGET_CLOSED", onCloseWrapper, { once: true });

                        timContainer.addEventListener("TIM_WIDGET_OFFER_CONFIRM", onSaveWrapper);
                    }
                }, // Error callback: doesn't return anything, though is triggered when something goes wrong.
                (_) => {
                    dispatch(
                        updateModalSettings(
                            { injectionLoading: false, injectionError: true },
                            MODAL_INSURANCE_CALCULATOR,
                        ),
                    );
                },
            );
        });
    };

    return { showCalculator, isT1InjectionReady, onClose, containerId };
};

export default useAphInsuranceCalculatorButton;
